// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Plus, Trash2, GripVertical, Loader2, Save, Pencil,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { RichTextEditor } from "@/design-system/forms/RichTextEditor";
import type {
  FormFieldConfig, FormFieldType, EventFormConfig,
} from "@/types/events";

const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select (dropdown)" },
  { value: "multi_select", label: "Multi-select" },
  { value: "textarea", label: "Long text" },
  { value: "checkbox", label: "Checkbox" },
  { value: "repeater", label: "Repeater (list)" },
];

function toKey(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || "field";
}

interface Props {
  eventId: string;
  formType: "coach_attendance" | "media_credentials";
  defaultFields: FormFieldConfig[];
  defaultTitle: string;
  defaultDescription: string;
  defaultThankYou?: string;
}

export function FormBuilder({
  eventId, formType, defaultFields, defaultTitle, defaultDescription, defaultThankYou,
}: Props) {
  const [config, setConfig] = useState<Partial<EventFormConfig> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailsText, setEmailsText] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("event_form_configs")
      .select("*")
      .eq("event_id", eventId)
      .eq("form_type", formType)
      .maybeSingle();
    setLoading(false);
    if (error && error.code !== "PGRST116") {
      toast.error("Failed to load form config");
      return;
    }
    if (data) {
      setConfig(data as any);
      setEmailsText(((data as any).notification_emails || []).join(", "));
    } else {
      setConfig({
        event_id: eventId,
        form_type: formType,
        fields: defaultFields,
        form_title: defaultTitle,
        form_description: defaultDescription,
        thank_you_message: defaultThankYou ?? "Thanks — your submission has been received.",
        notification_emails: [],
        is_enabled: true,
      });
      setEmailsText("");
    }
  };

  useEffect(() => {
    if (eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, formType]);

  const fields = (config?.fields ?? []) as FormFieldConfig[];

  const updateField = (idx: number, patch: Partial<FormFieldConfig>) => {
    setConfig((c) => {
      if (!c) return c;
      const next = [...(c.fields as FormFieldConfig[])];
      next[idx] = { ...next[idx], ...patch };
      return { ...c, fields: next };
    });
  };
  const removeField = (idx: number) => {
    setConfig((c) => {
      if (!c) return c;
      const next = [...(c.fields as FormFieldConfig[])];
      next.splice(idx, 1);
      return { ...c, fields: next.map((f, i) => ({ ...f, order: i + 1 })) };
    });
  };

  const moveField = (from: number, to: number) => {
    if (from === to) return;
    setConfig((c) => {
      if (!c) return c;
      const next = [...(c.fields as FormFieldConfig[])];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return { ...c, fields: next.map((f, i) => ({ ...f, order: i + 1 })) };
    });
  };

  const addField = (label: string, type: FormFieldType, required: boolean, options: string) => {
    const key = toKey(label);
    setConfig((c) => {
      if (!c) return c;
      const list = (c.fields as FormFieldConfig[]) ?? [];
      const newField: FormFieldConfig = {
        key,
        label,
        type,
        required,
        order: list.length + 1,
        ...(type === "select" || type === "multi_select"
          ? { options: options.split(",").map((o) => o.trim()).filter(Boolean) }
          : {}),
      };
      return { ...c, fields: [...list, newField] };
    });
    setShowAddDialog(false);
  };

  const save = async () => {
    if (!config) return;
    setSaving(true);
    const emails = emailsText
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const payload: any = {
      event_id: eventId,
      form_type: formType,
      fields: config.fields,
      form_title: config.form_title,
      form_description: config.form_description,
      thank_you_message: config.thank_you_message,
      notification_emails: emails,
      is_enabled: config.is_enabled ?? true,
    };
    const { error } = await supabase
      .from("event_form_configs")
      .upsert(payload, { onConflict: "event_id,form_type" });
    setSaving(false);
    if (error) {
      toast.error("Save failed: " + error.message);
      return;
    }
    toast.success("Form saved");
    load();
  };

  if (loading || !config) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Form meta */}
      <div className="space-y-3 p-4 rounded-lg border border-border bg-card">
        <div>
          <Label>Form title</Label>
          <Input
            value={config.form_title || ""}
            onChange={(e) => setConfig({ ...config, form_title: e.target.value })}
          />
        </div>
        <div>
          <Label>Form description</Label>
          <RichTextEditor
            value={config.form_description || ""}
            onChange={(html) => setConfig({ ...config, form_description: html })}
            placeholder="Shown above the form on the public page"
            minHeight="80px"
          />
        </div>
        <div>
          <Label>Thank you message</Label>
          <RichTextEditor
            value={config.thank_you_message || ""}
            onChange={(html) => setConfig({ ...config, thank_you_message: html })}
            placeholder="Shown after the form is submitted"
            minHeight="80px"
          />
        </div>
        <div>
          <Label>Notification emails (comma-separated)</Label>
          <Input
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="ops@example.com, director@example.com"
          />
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Fields ({fields.length})
          </h4>
          <button
            onClick={() => setShowAddDialog(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-input text-sm hover:bg-accent"
          >
            <Plus className="w-4 h-4" /> Add Field
          </button>
        </div>

        <div className="space-y-1.5">
          {fields.map((f, idx) => (
            <div
              key={f.key + idx}
              draggable
              onDragStart={() => setDraggingIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (draggingIdx !== null) moveField(draggingIdx, idx);
                setDraggingIdx(null);
              }}
              className="grid grid-cols-12 items-center gap-2 p-2 rounded-md border border-border bg-card"
            >
              <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab col-span-1" />
              <Input
                className="col-span-4 h-8"
                value={f.label}
                onChange={(e) => updateField(idx, { label: e.target.value })}
              />
              <select
                className="col-span-3 h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={f.type}
                onChange={(e) => updateField(idx, { type: e.target.value as FormFieldType })}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <label className="col-span-2 inline-flex items-center gap-1.5 text-xs">
                <Checkbox
                  checked={f.required}
                  onCheckedChange={(v) => updateField(idx, { required: !!v })}
                />
                Required
              </label>
              <div className="col-span-1 text-xs text-muted-foreground truncate">{f.key}</div>
              <div className="col-span-1 flex items-center justify-end gap-0.5">
                <button
                  onClick={() => setEditingIdx(idx)}
                  className="text-muted-foreground hover:text-foreground hover:bg-accent rounded p-1"
                  title="Edit details"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => removeField(idx)}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {(f.type === "select" || f.type === "multi_select") && (
                <div className="col-span-12 pl-6">
                  <Input
                    className="h-8 text-xs"
                    placeholder="Options (comma-separated)"
                    value={(f.options || []).join(", ")}
                    onChange={(e) =>
                      updateField(idx, {
                        options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean),
                      })
                    }
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save form
        </button>
      </div>

      {/* Add field dialog */}
      <AddFieldDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={addField}
      />

      {/* Edit field dialog (helper text, placeholder, etc.) */}
      <EditFieldDialog
        open={editingIdx !== null}
        field={editingIdx !== null ? fields[editingIdx] : null}
        onClose={() => setEditingIdx(null)}
        onSave={(patch) => {
          if (editingIdx !== null) updateField(editingIdx, patch);
          setEditingIdx(null);
        }}
      />
    </div>
  );
}

function EditFieldDialog({
  open, field, onClose, onSave,
}: {
  open: boolean;
  field: FormFieldConfig | null;
  onClose: () => void;
  onSave: (patch: Partial<FormFieldConfig>) => void;
}) {
  const [draft, setDraft] = useState<Partial<FormFieldConfig>>({});
  useEffect(() => {
    if (open && field) setDraft({ ...field });
  }, [open, field]);
  if (!field) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit field</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Label</Label>
            <Input
              value={(draft.label as string) || ""}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            />
          </div>
          <div>
            <Label>Placeholder</Label>
            <Input
              value={(draft.placeholder as string) || ""}
              onChange={(e) => setDraft({ ...draft, placeholder: e.target.value })}
            />
          </div>
          <div>
            <Label>Helper text (shown below the field)</Label>
            <RichTextEditor
              value={(draft.helper_text as string) || ""}
              onChange={(html) => setDraft({ ...draft, helper_text: html })}
              placeholder="Optional guidance for the person filling out this field"
              minHeight="80px"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={!!draft.required}
              onCheckedChange={(v) => setDraft({ ...draft, required: !!v })}
            />
            Required
          </label>
        </div>
        <DialogFooter>
          <button className="px-3 py-2 rounded-md border border-input text-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm"
            onClick={() => onSave(draft)}
          >
            Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddFieldDialog({
  open, onClose, onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (label: string, type: FormFieldType, required: boolean, options: string) => void;
}) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<FormFieldType>("text");
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState("");
  useEffect(() => {
    if (open) { setLabel(""); setType("text"); setRequired(false); setOptions(""); }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add custom field</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Label *</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            {label && (
              <p className="text-xs text-muted-foreground mt-1">key: {toKey(label)}</p>
            )}
          </div>
          <div>
            <Label>Type</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as FormFieldType)}
              className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          {(type === "select" || type === "multi_select") && (
            <div>
              <Label>Options (comma-separated)</Label>
              <Input value={options} onChange={(e) => setOptions(e.target.value)} />
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox checked={required} onCheckedChange={(v) => setRequired(!!v)} />
            Required
          </label>
        </div>
        <DialogFooter>
          <button className="px-3 py-2 rounded-md border border-input text-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={!label.trim()}
            onClick={() => onAdd(label.trim(), type, required, options)}
            className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
          >
            Add field
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
